import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SnackbarOrigin, Stack } from '@mui/material';
import { SnackbarMessage, useSnackbar } from 'notistack';
import { v4 } from 'uuid';

import { Button, ButtonProps, IconButton, RouterButton, RouterButtonProps } from '@components/base';

type SnackAction = Omit<Partial<RouterButtonProps> & ButtonProps, 'children'> & {
  label: string;
};

export type SnackVariant = 'error' | 'info' | 'success' | undefined;

type SnackbarOptions = {
  actions?: SnackAction | SnackAction[];
  closeOnUnmount?: boolean;
  dismissable?: boolean;
  onUndo?: VoidFunction;
  persist?: boolean;
  position?: SnackbarOrigin['horizontal'];
  variant?: SnackVariant;
};

const isLink = (item: object): item is RouterButtonProps => 'to' in item;

const useSnack = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar: closeSnack } = useSnackbar();

  const [keys, setKeys] = useState<string[]>([]);

  const keyRefs = useRef(keys);

  useEffect(() => {
    keyRefs.current = keys;
  }, [keys]);

  const handleCloseSnack = useCallback(
    (key: string) => {
      closeSnack(key);
      setKeys(prev => prev.filter(k => k !== key));
    },
    [closeSnack]
  );

  const addKey = (key: string) => setKeys(prev => [...prev, key]);

  useEffect(() => {
    return () => {
      keyRefs.current.forEach(key => handleCloseSnack(key));
    };
  }, [handleCloseSnack]);

  const addSnack = (message: SnackbarMessage, options?: SnackbarOptions) => {
    const {
      actions,
      closeOnUnmount,
      dismissable = true,
      onUndo,
      persist,
      variant,
      position = 'left',
    } = options || {};

    const key = v4();

    if (closeOnUnmount) addKey(key);

    enqueueSnackbar(message, {
      key,
      autoHideDuration: 6000,
      anchorOrigin: { horizontal: position, vertical: 'bottom' },
      action:
        dismissable || actions || onUndo ? (
          <Stack direction='row'>
            {actions &&
              (Array.isArray(actions) ? actions : [actions]).map(
                ({ color = 'inherit', label, size = 'small', ...rest }) =>
                  isLink(rest) ? (
                    <RouterButton color={color} key={label} size={size} {...rest}>
                      {label}
                    </RouterButton>
                  ) : (
                    <Button color={color} key={label} size={size} {...rest}>
                      {label}
                    </Button>
                  )
              )}

            {onUndo && (
              <Button
                color='inherit'
                onClick={() => {
                  onUndo();
                  handleCloseSnack(key);
                }}
                size='small'
              >
                {t('undo')}
              </Button>
            )}

            {dismissable && (
              <IconButton
                aria-label='dismiss'
                color='inherit'
                icon='close'
                onClick={() => handleCloseSnack(key)}
              />
            )}
          </Stack>
        ) : undefined,
      persist,
      preventDuplicate: true,
      variant,
    });

    return key;
  };

  return { addSnack, closeSnack: handleCloseSnack };
};

export default useSnack;
